import dayjs, { type Dayjs } from 'dayjs';
import duration from 'dayjs/plugin/duration';
import DOMPurify from 'dompurify';
import { useEffect, useState, type RefObject } from 'react';

dayjs.extend(duration);

declare global {
  interface String {
    toPersianDigit(): string;
    toEnglishDigit(): string;
  }
}

export const isIterableArray = <T,>(array: T[] | null | undefined): boolean =>
  Array.isArray(array) && !!array.length;

//===============================
// Breakpoints
//===============================
export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1540
} as const;

export const breakpointsMediaQuery = {
  xs: `(min-width:${breakpoints.xs}px)`,
  sm: `(min-width:${breakpoints.sm}px)`,
  md: `(min-width:${breakpoints.md}px)`,
  lg: `(min-width:${breakpoints.lg}px)`,
  xl: `(min-width:${breakpoints.xl}px)`,
  xxl: `(min-width:${breakpoints.xxl}px)`
} as const;

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(window.matchMedia(query).matches);

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    const documentChangeHandler = (event: MediaQueryListEvent) =>
      setMatches(event.matches);

    mediaQueryList.addEventListener('change', documentChangeHandler);
    return () => {
      mediaQueryList.removeEventListener('change', documentChangeHandler);
    };
  }, [query]);

  return matches;
}

export const getItemFromStore = <T,>(
  key: string,
  defaultValue: T,
  store: Storage = localStorage
): T => {
  try {
    return store.getItem(key) === null
      ? defaultValue
      : (JSON.parse(store.getItem(key) as string) as T);
  } catch {
    return (store.getItem(key) as T) || defaultValue;
  }
};

export const setItemToStore = (
  key: string,
  payload: string,
  store: Storage = localStorage
): void => {
  store.setItem(key, payload);
};

export const getStoreSpace = (store: Storage = localStorage): number => {
  const escapeFn = (
    globalThis as typeof globalThis & { escape: (str: string) => string }
  ).escape;

  return parseFloat(
    (escapeFn(encodeURIComponent(JSON.stringify(store))).length / (1024 * 1024)).toFixed(
      2
    )
  );
};

//===============================
// Cookie
//===============================
export const getCookieValue = (name: string): string | null => {
  const value = document.cookie.match(
    '(^|[^;]+)\\s*' + name + '\\s*=\\s*([^;]+)'
  );
  return value ? value.pop() ?? null : null;
};

export const createCookie = (
  name: string,
  value: string,
  cookieExpireTime: number
): void => {
  const date = new Date();
  date.setTime(date.getTime() + cookieExpireTime);
  const expires = '; expires=' + date.toUTCString();
  document.cookie = name + '=' + value + expires + '; path=/';
};

export const numberFormatter = (
  number: number | string,
  fixed = 2
): string => {
  return Math.abs(Number(number)) >= 1.0e9
    ? (Math.abs(Number(number)) / 1.0e9).toFixed(fixed) + 'B'
    : Math.abs(Number(number)) >= 1.0e6
      ? (Math.abs(Number(number)) / 1.0e6).toFixed(fixed) + 'M'
      : Math.abs(Number(number)) >= 1.0e3
        ? (Math.abs(Number(number)) / 1.0e3).toFixed(fixed) + 'K'
        : Math.abs(Number(number)).toFixed(fixed);
};

//===============================
// Colors
//===============================
export const hexToRgb = (hexValue: string): [number, number, number] | null => {
  let hex: string;
  hexValue.indexOf('#') === 0
    ? (hex = hexValue.substring(1))
    : (hex = hexValue);
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
    hex.replace(shorthandRegex, (_m, r, g, b) => r + r + g + g + b + b)
  );
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
      ]
    : null;
};

export const colors = [
  '#2c7be5',
  '#00d97e',
  '#e63757',
  '#39afd1',
  '#fd7e14',
  '#02a8b5',
  '#727cf5',
  '#6b5eae',
  '#ff679b',
  '#f6c343'
];

export const rgbColor = (color = colors[0]): string =>
  `rgb(${hexToRgb(color)})`;
export const rgbaColor = (color = colors[0], alpha = 0.5): string =>
  `rgba(${hexToRgb(color)},${alpha})`;

export const themeColors = {
  primary: '#2c7be5',
  secondary: '#748194',
  success: '#00d27a',
  info: '#27bcfd',
  warning: '#f5803e',
  danger: '#e63757',
  light: '#f9fafd',
  dark: '#0b1727'
};

export const grays = {
  white: '#fff',
  100: '#f9fafd',
  200: '#edf2f9',
  300: '#d8e2ef',
  400: '#b6c1d2',
  500: '#9da9bb',
  600: '#748194',
  700: '#5e6e82',
  800: '#4d5969',
  900: '#344050',
  1000: '#232e3c',
  1100: '#0b1727',
  black: '#000'
};

export const darkGrays = {
  white: '#fff',
  1100: '#f9fafd',
  1000: '#edf2f9',
  900: '#d8e2ef',
  800: '#b6c1d2',
  700: '#9da9bb',
  600: '#748194',
  500: '#5e6e82',
  400: '#4d5969',
  300: '#344050',
  200: '#232e3c',
  100: '#0b1727',
  black: '#000'
};

export const getGrays = (isDark: boolean) => (isDark ? darkGrays : grays);

export function darkenColor(
  rgb: number[] | string,
  amount = 30
): string {
  let [r, g, b] = Array.isArray(rgb)
    ? rgb
    : rgb.split(',').map(Number);

  r = Math.max((r ?? 0) - amount, 0);
  g = Math.max((g ?? 0) - amount, 0);
  b = Math.max((b ?? 0) - amount, 0);

  return `${r}, ${g}, ${b}`;
}

export const rgbColors = colors.map(color => rgbColor(color));
export const rgbaColors = colors.map(color => rgbaColor(color));

const isColorName = (colorName: string): boolean => {
  const s = new Option().style;
  s.color = colorName;
  return s.color !== '';
};

export const getColor = (
  name: string,
  dom: HTMLElement = document.documentElement
): string => {
  if (
    name.startsWith('#') ||
    name.startsWith('rgb') ||
    name.startsWith('hsl') ||
    isColorName(name)
  ) {
    return name;
  }

  const color = getComputedStyle(dom)
    .getPropertyValue(`--falcon-${name}`)
    .trim();
  return color || name;
};

//===============================
// Echarts
//===============================
export const getPosition = (
  pos: [number, number],
  _params: unknown,
  _dom: unknown,
  _rect: unknown,
  size: { contentSize: [number, number] }
) => ({
  top: pos[1] - size.contentSize[1] - 10,
  left: pos[0] - size.contentSize[0] / 2
});

//===============================
// E-Commerce
//===============================
type CartItem = { id: string | number; quantity: number };
type BaseItem = { id: string | number; price: number; sale?: number };
type PricedItem = { price: number; quantity: number };

export const calculateSale = (
  base: number,
  less = 0,
  fix = 2
): string => (base - base * (less / 100)).toFixed(fix);

export const getTotalPrice = (
  cart: CartItem[],
  baseItems: BaseItem[]
): number =>
  cart.reduce((accumulator, currentValue) => {
    const { id, quantity } = currentValue;
    const item = baseItems.find(baseItem => baseItem.id === id);
    if (!item) return accumulator;
    const { price, sale } = item;
    return accumulator + Number(calculateSale(price, sale)) * quantity;
  }, 0);

export const getSubtotal = (items: PricedItem[]): number =>
  items.reduce((acc, curr) => curr.price * curr.quantity + acc, 0);

export const getDiscountPrice = (total: number, discount: number): number =>
  total - total * (discount / 100);

export const getProductsQuantity = (
  products: { quantity: number }[]
): number => products.reduce((acc, product) => product.quantity + acc, 0);

//===============================
// Helpers
//===============================
export const getPaginationArray = (
  totalSize: number,
  sizePerPage: number
): number[] => {
  const noOfPages = Math.ceil(totalSize / sizePerPage);
  const array: number[] = [];
  let pageNo = 1;
  while (pageNo <= noOfPages) {
    array.push(pageNo);
    pageNo = pageNo + 1;
  }
  return array;
};

export const capitalize = (str: string): string =>
  (str.charAt(0).toUpperCase() + str.slice(1)).replace(/-/g, ' ');

export const camelize = (str: string): string => {
  return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index: number) => {
    if (+match === 0) return '';
    return index === 0 ? match.toLowerCase() : match.toUpperCase();
  });
};

export const dashed = (str: string): string => {
  return str.toLowerCase().replaceAll(' ', '-');
};

type RouteNode = {
  name?: string;
  to?: string;
  children?: RouteNode[];
};

export const flatRoutes = (childrens: RouteNode[]): RouteNode[] => {
  const allChilds: RouteNode[] = [];

  const flatChild = (nodes: RouteNode[]) => {
    nodes.forEach(child => {
      if (child.children) {
        flatChild(child.children);
      } else {
        allChilds.push(child);
      }
    });
  };
  flatChild(childrens);

  return allChilds;
};

type SelectedDomain = { slug?: string; name?: string };

export const routeToHandler = (
  route: RouteNode | null | undefined,
  selectedDomain: SelectedDomain,
  domain?: string,
  domain_slug?: string,
  slug?: string,
  loc?: string,
  lang?: string,
  keyword?: string
): string | undefined => {
  if (!route?.to) return undefined;

  // The backlink parent opens its tabs; the parent path must not inherit a
  // domain path before a tab is selected.
  if (route.to === '/backlink' || route.to === '/backlink/') {
    return route.to;
  }

  if (route.to.includes('content-tools/ai-analysis')) {
    if (route.to.includes('history')) {
      return `${route.to}/${selectedDomain.slug}/${selectedDomain.name}`;
    }
    return `${route.to}/${domain_slug ? domain_slug : selectedDomain.slug}/${domain ? domain : selectedDomain.name}/${slug ? slug : 'last'}`;
  }

  if (route.to.includes('crawl') || route.to.includes('analysis/')) {
    if (route.to.includes('history')) {
      return `${route.to}/${selectedDomain.slug}/${selectedDomain.name}`;
    }
    return `${route.to}/${slug ? slug : 'last'}/${domain_slug ? domain_slug : selectedDomain.slug}/${domain ? domain : selectedDomain.name}`;
  }

  if (
    route.to.includes('keyword-research') ||
    route.to.includes('keyword-tracker') ||
    route.to.includes('tracker-ai') ||
    route.to.includes('reporting') ||
    route.to.includes('backlink') ||
    route.to.includes('indexer')
  ) {
    return `${route.to}/${domain_slug ? domain_slug : selectedDomain.slug}/${domain ? domain : selectedDomain.name}`;
  }

  if (route.to.includes('serp')) {
    if (route.to.includes('history')) {
      return `${route.to}/${selectedDomain.slug}/${selectedDomain.name}`;
    }
    return `${route.to}/${domain_slug ? domain_slug : selectedDomain.slug}/${domain ? domain : selectedDomain.name}/${slug ? slug : 'last'}/${loc ? loc : 'last'}/${lang ? lang : 'last'}/${keyword ? keyword : 'last'}`;
  }

  return route.to;
};

export const getFlatRoutes = (
  children: RouteNode[]
): Record<string, RouteNode[]> =>
  children.reduce(
    (acc, val) => {
      if (val.children) {
        return {
          ...acc,
          [camelize(val.name ?? '')]: flatRoutes(val.children)
        };
      }
      return {
        ...acc,
        unTitled: [...acc.unTitled, val]
      };
    },
    { unTitled: [] as RouteNode[] }
  );

export const routesSlicer = ({
  routes,
  columns = 3,
  rows
}: {
  routes: RouteNode[];
  columns?: number;
  rows?: number;
}): RouteNode[][] => {
  const routesCollection: RouteNode[] = [];
  routes.forEach(route => {
    if (route.children) {
      route.children.forEach(item => {
        if (item.children) {
          routesCollection.push(...item.children);
        } else {
          routesCollection.push(item);
        }
      });
    } else {
      routesCollection.push(route);
    }
  });

  const totalRoutes = routesCollection.length;
  const calculatedRows = rows || Math.ceil(totalRoutes / columns);
  const routesChunks: RouteNode[][] = [];
  for (let i = 0; i < totalRoutes; i += calculatedRows) {
    routesChunks.push(routesCollection.slice(i, i + calculatedRows));
  }
  return routesChunks;
};

export const getPageName = (pageName: string): boolean => {
  return window.location.pathname.split('/').slice(-1)[0] === pageName;
};

export const copyToClipBoard = (
  textFieldRef: RefObject<HTMLInputElement | HTMLTextAreaElement | null>
): void => {
  const textField = textFieldRef.current;
  if (!textField) return;
  textField.focus();
  textField.select();
  document.execCommand('copy');
};

export const reactBootstrapDocsUrl = 'https://react-bootstrap.github.io';

export const pagination = (currentPage: number, size: number): number[] => {
  const pages = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  let prev = currentPage - 1 - Math.floor(size / 2);

  if (currentPage - 1 - Math.floor(size / 2) < 0) {
    prev = 0;
  }
  if (currentPage - 1 - Math.floor(size / 2) > pages.length - size) {
    prev = pages.length - size;
  }
  const next = prev + size;

  return pages.slice(prev, next);
};

export const toPersianDigit = (value: string): string => {
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return value.replace(/[0-9]/g, digit => persianDigits[Number(digit)]);
};

if (!String.prototype.toPersianDigit) {
  String.prototype.toPersianDigit = function () {
    return toPersianDigit(String(this));
  };
}

String.prototype.toEnglishDigit = function () {
  const find = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  const replace = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  let replaceString = String(this);
  for (let i = 0; i < find.length; i++) {
    const regex = new RegExp(find[i], 'g');
    replaceString = replaceString.replace(regex, replace[i]);
  }
  return replaceString;
};

export const isHTMLString = (str: string): boolean => {
  return /<[a-z][\s\S]*>/i.test(str);
};

export const htmlParserFunc = (html: string): string => {
  let decodeHtml = '';
  try {
    decodeHtml = decodeURIComponent(html);
  } catch {
    decodeHtml = html;
  }
  DOMPurify.addHook('afterSanitizeAttributes', node => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
  return DOMPurify.sanitize(decodeHtml, {
    USE_PROFILES: { html: true }
  });
};

export const decoder = (encodedString: string): string => {
  try {
    return decodeURIComponent(encodedString);
  } catch (error) {
    console.log(error);
    return encodedString;
  }
};

export const removeHTMLTags = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

type TooltipParam = {
  borderColor?: string;
  color?: string;
  seriesName?: string;
  value?: unknown;
  axisValue?: string | number;
};

export const tooltipFormatter = (params: TooltipParam[]): string => {
  let tooltipItem = ``;
  params.forEach(el => {
    tooltipItem =
      tooltipItem +
      `<div class='ms-1'> 
        <h6 class="text-700"><span class="fa fa-circle me-1 fs--2" style="color:${
          el.borderColor ? el.borderColor : el.color
        }"></span>
          ${el.seriesName} : ${
            el.value
              ? typeof el.value === 'object' && Array.isArray(el.value)
                ? el.value[1] !== null && el.value[1] !== undefined
                  ? el.value[1]
                  : ''
                : el.value
              : ''
          }
        </h6>
      </div>`
        ?.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')
        ?.toPersianDigit();
  });
  return `<div>
            <p class='mb-2 text-600'>
              ${
                dayjs(params[0].axisValue).isValid()
                  ? dayjs(params[0].axisValue).format('MMMM DD')
                  : params[0].axisValue
              }
            </p>
            ${tooltipItem}
          </div>`.toPersianDigit();
};

export const percentageOfAll = (num: number, all: number): string => {
  return (num * (100 / all))?.toFixed(2);
};

export const addIdField = <T extends object>(
  items: T[]
): (T & { id: number })[] => {
  return items.map((item, index) => ({
    id: index + 1,
    ...item
  }));
};

const colorsArr = [
  '#e2cc9e',
  '#3b82f6',
  '#10b981',
  '#f97316',
  '#ec4899',
  '#a855f7',
  '#ef4444',
  '#ffd300',
  '#795548',
  '#607d8b',
  '#CB410B'
];
let colorIndex = 0;

export const getRandomColor = (): string => {
  const color = colorsArr[colorIndex];
  colorIndex = (colorIndex + 1) % colorsArr.length;
  return color;
};

export const colorByIndex = (i: number): string => {
  const index = ((i % colorsArr.length) + colorsArr.length) % colorsArr.length;
  return colorsArr[index];
};

export const variantByIndex = (i: number): string => {
  switch (i) {
    case 0:
      return 'primary';
    case 1:
      return 'info';
    case 2:
      return 'danger';
    case 3:
      return 'success';
    case 4:
      return 'warning';
    case 5:
      return 'secondary';
    default:
      return 'secondary';
  }
};

export const getSize = (size: number): string => {
  if (size < 1024) {
    return `${size} Byte`;
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  }
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

export const getRandomNumber = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min) + min);
};

export const getDates = (
  startDate: Date,
  endDate: Date,
  interval = 1000 * 60 * 60 * 24
): Date[] => {
  const durationMs = endDate.getTime() - startDate.getTime();
  const steps = durationMs / interval;
  return Array.from(
    { length: steps + 1 },
    (_v, i) => new Date(startDate.valueOf() + interval * i)
  );
};

export const getPastDates = (duration: string | number): Date[] => {
  let days: number;

  switch (duration) {
    case 'week':
      days = 7;
      break;
    case 'month':
      days = 30;
      break;
    case 'year':
      days = 365;
      break;
    default:
      days = Number(duration);
  }

  const date = new Date();
  const endDate = date;
  const startDate = new Date(new Date().setDate(date.getDate() - (days - 1)));
  return getDates(startDate, endDate);
};

export const addId = <T extends object>(
  items: T[]
): (T & { id: number })[] =>
  items.map((item, index) => ({
    id: index + 1,
    ...item
  }));

export const getTimeDuration = (
  startDate: Dayjs,
  endDate: Dayjs,
  format = ''
): string => {
  return dayjs.duration(endDate.diff(startDate)).format(format);
};

export const getPercentage = (
  number: number | string,
  percent: number | string
): number => {
  return (Number(number) / 100) * Number(percent);
};

export const chunk = <T,>(
  arr: T[],
  chunkSize = 1,
  cache: T[][] = []
): T[][] => {
  const tmp = [...arr];
  if (chunkSize <= 0) return cache;
  while (tmp.length) cache.push(tmp.splice(0, chunkSize));
  return cache;
};

export const slugifyText = (str: string): string =>
  str
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');

export function formatMinutes(minutes?: number | null): string {
  if (!minutes) return '00:00:30';

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours === 0 && remainingMinutes === 0) {
    return '00:00:30';
  }

  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = remainingMinutes.toString().padStart(2, '0');
  const formattedSeconds = '00';

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

export function urlToArray(url: string): string[] {
  const isHttps = url.includes('https://');
  const parts = url
    .slice(isHttps ? 8 : 7)
    .split('/')
    .filter(Boolean);
  return [
    isHttps ? 'https://' : 'http://',
    ...parts.map((part, index) =>
      index === 0 ? part : part.startsWith('?') ? '' : `${part}`
    )
  ];
}

type AgGridRowParams = {
  api: {
    paginationGetCurrentPage: () => number;
    paginationGetPageSize: () => number;
    getDisplayedRowCount: () => number;
  };
  node: {
    rowIndex: number | null;
  };
};

export const isLastRow = (params: AgGridRowParams): boolean => {
  const api = params.api;
  const currentPage = api.paginationGetCurrentPage();
  const pageSize = api.paginationGetPageSize();
  const lastRowIndex =
    Math.min((currentPage + 1) * pageSize, api.getDisplayedRowCount()) - 1;
  const rowIndex = params.node.rowIndex;
  return rowIndex === lastRowIndex;
};

export const isFirstRow = (params: AgGridRowParams): boolean => {
  const api = params.api;
  const currentPage = api.paginationGetCurrentPage();
  const pageSize = api.paginationGetPageSize();
  const firstRowIndex = currentPage * pageSize;
  const rowIndex = params.node.rowIndex;
  return rowIndex === firstRowIndex;
};
